/**
 * Monotonic elapsed-time helpers for telemetry durations.
 *
 * Why this exists (measured on production PostHog, project 375980, 2026-08-30):
 * `auth_load_failed.waited_ms` was computed as `Date.now() - startedAt`, and
 * production carried values that are arithmetically impossible -- three events
 * on 2026-08-29 reported `reason: 'timeout'` with waited_ms of 1833, 2001 and
 * 1982 against a CLERK_LOAD_TIMEOUT_MS of 10_000. A ten-second timer cannot
 * fire two seconds after its own instance mounted, so the metric was wrong
 * rather than the timer being fast.
 *
 * `Date.now()` reads the WALL clock, which is not monotonic: an NTP correction,
 * a device sleep/wake, a timezone-daemon adjustment or a user editing the clock
 * all move it, in either direction, mid-measurement. Subtracting two readings
 * across such a jump yields a duration that never elapsed. Reproduced exactly
 * in jest: an 8s backwards jump during a 10s wait yields waited_ms = 2000.
 *
 * Correction 2026-09-13: the monotonic clock is still the right clock, but
 * "the timer cannot be fast" was too strong. With this helper deployed
 * (6bc9e775), production recorded `reason: 'timeout'` with waited_ms 1900 at
 * 2026-09-11 17:46:21Z, from a session carrying every mark of a mail-security
 * link sandbox: viewport equal to a 1920x1080 screen, America/Los_Angeles
 * behind a UK IP, `$direct`, the same IP and window id as a neighbouring
 * anonymous person, and the CefSharp "Object Not Found Matching Id" rejection
 * ten seconds later in the same session. No real browser fires a 10s timer
 * after 1.9s of monotonic time; whatever the sandbox does to its clocks is
 * not observable from our events. So for analytics, `reason = 'timeout' AND
 * waited_ms < CLERK_LOAD_TIMEOUT_MS` is a scanner fingerprint, not a metric
 * bug to chase again.
 *
 * `performance.now()` is monotonic by specification (High Resolution Time,
 * https://w3c.github.io/hr-time/#dfn-monotonic-clock) and is the correct clock
 * for measuring a duration. It is unaffected by system clock changes.
 */

/**
 * A reading from the monotonic clock, for later use with `elapsedMsSince`.
 *
 * The fallback is deliberate but narrow: `performance` is present in every
 * browser we support and in jsdom, so in practice this always takes the
 * monotonic path. Falling back to `Date.now()` in a hypothetical environment
 * without it keeps the caller working (a slightly wrong duration beats a
 * TypeError in a telemetry line) and is why callers must pair this with
 * `elapsedMsSince` rather than mixing clocks by hand.
 */
export function monotonicNow(): number {
  if (typeof performance !== 'undefined' && typeof performance.now === 'function') {
    return performance.now();
  }
  return Date.now();
}

/**
 * Whole milliseconds elapsed since a `monotonicNow()` reading.
 *
 * Rounded because `performance.now()` is fractional while every existing
 * consumer of these properties (and every historical event already in PostHog)
 * carries an integer, and clamped at zero so a caller can never publish a
 * negative duration.
 */
export function elapsedMsSince(startedAt: number): number {
  return Math.max(0, Math.round(monotonicNow() - startedAt));
}
